import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Turnstile } from '@marsidev/react-turnstile';

const categories = [
  { value: 'feedback', label: 'Σχόλιο' },
  { value: 'suggestion', label: 'Πρόταση' },
  { value: 'question', label: 'Ερώτηση' },
];

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { user, clinic } = useAuth();
  const [category, setCategory] = useState('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [turnstileToken, setTurnstileToken] = useState('');
  const toastTimeoutRef = useRef(null);
  const turnstileRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message: msg, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const resetForm = () => {
    setCategory('feedback');
    setSubject('');
    setMessage('');
    setTurnstileToken('');
    turnstileRef.current?.reset();
  };

  const handleClose = () => {
    if (isSending) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      showToast('Παρακαλώ εισάγετε θέμα', 'error');
      return;
    }
    if (!message.trim()) {
      showToast('Παρακαλώ εισάγετε μήνυμα', 'error');
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-form', {
        body: {
          category,
          subject: subject.trim(),
          message: message.trim(),
          user_email: user?.email,
          clinic_name: clinic?.name || null,
          turnstile_token: turnstileToken,
        },
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'Unknown error');

      showToast('Το μήνυμά σας στάλθηκε επιτυχώς! Θα σας απαντήσουμε σύντομα.', 'success');
      resetForm();
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Contact form error:', err);
      showToast('Σφάλμα κατά την αποστολή. Δοκιμάστε ξανά.', 'error');
    }
    setIsSending(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={handleClose}
      >
        <div
          className="rounded-2xl border max-w-lg w-full flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          style={{ backgroundColor: '#111827', borderColor: '#374151' }}
        >
          {/* Header */}
          <div className="p-6 border-b flex-shrink-0 flex items-center justify-between" style={{ borderColor: '#374151' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.15)' }}>
                <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Επικοινωνία</h2>
                <p className="text-gray-400 text-xs mt-0.5">Στείλτε μας τα σχόλια ή τις προτάσεις σας.</p>
              </div>
            </div>
            <button onClick={handleClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-gray-800 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="p-6 space-y-5">
            <div>
              <label className="block text-gray-400 text-sm font-medium mb-1.5">Κατηγορία</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-colors"
                style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-400 text-sm font-medium mb-1.5">Θέμα</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Περιγράψτε σύντομα το θέμα..."
                className="w-full px-4 py-2.5 rounded-lg border text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-colors"
                style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
              />
            </div>

            <div>
              <label className="block text-gray-400 text-sm font-medium mb-1.5">Μήνυμα</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Γράψτε το μήνυμά σας..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg border text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none transition-colors"
                style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
              />
            </div>

            {/* Turnstile Container */}
            <div className="flex justify-start">
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onError={() => setTurnstileToken('')}
                onExpire={() => setTurnstileToken('')}
                theme="dark"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t flex gap-3 flex-shrink-0 bg-[#1F2937]/30 rounded-b-2xl" style={{ borderColor: '#374151' }}>
            <button
              onClick={handleClose}
              disabled={isSending}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-gray-600 bg-[#111827] hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              Ακύρωση
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSending || !turnstileToken}
              className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-md flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
            >
              {isSending ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Αποστολή...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Αποστολή
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 left-4 sm:left-auto z-[60]">
          <div
            className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
              backdropFilter: 'blur(10px)',
              borderColor: toast.type === 'success' ? '#10B981' : '#EF4444',
            }}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${toast.type === 'success' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              <svg className={`w-5 h-5 ${toast.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={toast.type === 'success' ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"} />
              </svg>
            </div>
            <div className="text-white font-medium flex-1 text-sm">{toast.message}</div>
            <button
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="ml-4 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactFormDialog;